import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../../config/baseUrl";
import axios from "axios";
import toastify from "../../config/toastify";

const normalizeBannerType = (type) => {
  return type === "daily_mock" ? "daily_mock" : "main";
};

export default function BannerFormPage({
  mode,
  bannerType,
  pageTitle,
  breadcrumbTitle,
  backPath,
  includeLink = false,
  successMessage,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formval, setFormval] = useState({
    name: "",
    link: "",
    status: "active",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [pageLoading, setPageLoading] = useState(mode === "edit");

  const changeHandler = (e) => {
    setFormval((preVal) => ({ ...preVal, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    if (mode !== "edit" || !id) {
      return;
    }

    const fetchBanner = async () => {
      try {
        setPageLoading(true);
        const response = await axios.get(`${baseUrl}/banner/${id}`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        });

        const bannerData = response?.data?.data;
        if (!bannerData) {
          toastify.error("Banner not found");
          navigate(backPath);
          return;
        }

        const isMainBanner =
          bannerData?.bannerFor === "normal" ||
          bannerData?.type === "main" ||
          bannerData?.type === "homepage" ||
          bannerData?.type === "other" ||
          bannerData?.bannerFor !== "daily_mock";

        if (!isMainBanner) {
          toastify.error("Banner type mismatch");
          navigate(backPath);
          return;
        }

        setFormval({
          name: bannerData?.name || "",
          link: bannerData?.link || "",
          status: bannerData?.status || "active",
        });
        setExistingImage(bannerData?.image ? `${baseUrl}/${bannerData.image}` : "");
      } catch (error) {
        console.error("Error :", error);
        toastify.error("Failed to fetch banner");
        navigate(backPath);
      } finally {
        setPageLoading(false);
      }
    };

    fetchBanner();
  }, [backPath, bannerType, id, mode, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formval.name) {
      return toastify.error("Name is required");
    }

    if (includeLink && !formval.link) {
      return toastify.error("Link is required");
    }

    if (mode === "add" && !selectedFile) {
      return toastify.error("Image is required");
    }

    let formData = new FormData();
    formData.append("name", formval.name);
    formData.append("status", formval.status);
    formData.append("bannerFor", "normal");
    formData.append("type", "homepage");

    if (includeLink) {
      formData.append("link", formval.link);
    }

    if (selectedFile) {
      formData.append("image", selectedFile);
    }

    try {
      const response =
        mode === "edit"
          ? await axios.put(`${baseUrl}/banner/${id}`, formData, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            })
          : await axios.post(`${baseUrl}/banner`, formData, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            });

      if (!response?.data?.status) {
        toastify.error(response?.data?.message || "Some error occurred");
        return;
      }

      toastify.success(successMessage);
      navigate(backPath);
    } catch (error) {
      console.error("Error :", error);
      toastify.error(error?.response?.data?.message || "Server error");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  if (pageLoading) {
    return (
      <section className="main-sec">
        <div className="row justify-content-center">
          <div className="col-lg-6 text-center py-5">
            <h5>Loading...</h5>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="main-sec">
        <div className="row align-items-center">
          <div className="col-lg-6">
            <div className="dashboard-title">
              <h4 className="dash-head">
                <i className="fa fa-users me-2" />
                {pageTitle}
              </h4>
            </div>
            <div className="custom-bredcump">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {breadcrumbTitle}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="col-lg-6 d-flex justify-content-end">
            <Link to={backPath} className="btn btn-info text-white">
              <i className="fa fa-arrow-left me-1"></i>
              Back
            </Link>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="cards edit-usr">
              <form action="" onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6 mb-4">
                    <label htmlFor="name" className="form-label">
                      Name<sup className="text-danger">*</sup>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder="Enter Name"
                      onChange={changeHandler}
                      value={formval?.name}
                    />
                  </div>

                  {includeLink && (
                    <div className="col-lg-6 mb-4">
                      <label htmlFor="link" className="form-label">
                        Link<sup className="text-danger">*</sup>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="link"
                        placeholder="Enter Link"
                        onChange={changeHandler}
                        value={formval?.link}
                      />
                    </div>
                  )}

                  <div className="col-lg-6 mb-4">
                    <label htmlFor="select-user" className="form-label">
                      Status<sup className="text-danger">*</sup>
                    </label>
                    <select
                      className="form-control"
                      name="status"
                      onChange={changeHandler}
                      value={formval?.status}
                    >
                      <option value={""}>Select Status</option>
                      <option value={"active"}>Active</option>
                      <option value={"inactive"}>Inactive</option>
                    </select>
                  </div>
                  <div className="col-lg-12 mb-4">
                    <label htmlFor="icon" className="form-label">
                      Image<sup className="text-danger">*</sup>
                    </label>

                    {mode === "edit" && existingImage && !selectedFile && (
                      <div className="mb-3">
                        <img
                          src={existingImage}
                          alt="Existing banner"
                          className="file-preview"
                          style={{ maxWidth: "200px", maxHeight: "200px" }}
                        />
                      </div>
                    )}

                    <div className="file-upload-container">
                      <div
                        className={`file-upload-area ${isDragging ? "dragging" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        {!selectedFile ? (
                          <>
                            <div className="file-upload-icon">
                              <i className="fa fa-cloud-upload"></i>
                            </div>
                            <div className="file-upload-text">
                              Drag & Drop your image here
                            </div>
                            <div className="file-upload-subtext">
                              or click to browse files
                            </div>
                            <input
                              type="file"
                              className="file-upload-input"
                              id="icon"
                              name="icon"
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                          </>
                        ) : (
                          <div className="file-preview-container">
                            <img
                              src={previewUrl || "/placeholder.svg"}
                              alt="Preview"
                              className="file-preview"
                            />
                            <button
                              type="button"
                              className="file-remove-btn"
                              onClick={removeFile}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                            <div className="file-info">
                              {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-12 text-center">
                    <button type="Submit" className="btn btn-for-add text-white">
                      <i className="fa fa-save me-1"></i>
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
